import React, { FC, useState, useEffect } from 'react'
import { CreateFlow, OnRepeat, OnSave } from '@ally/create-flow'
import {
  Transfer,
  saveTransfer,
  CustomerLimitsProvider,
} from '@ally/transfers-toolkit'
import {
  getAllTransferAccounts,
  withTransferAccountsLoadState,
} from '@ally/data-selectors-transfer-accounts'
import { Alert, useFocus } from '@ally/metronome-ui'
import LoadingSpinner from '../LoadingSpinner'
import Widget from '../../Widget'
import { useHostServices } from '../../../HostServices'
import { Input } from './Input'
import { Review } from './Review'
import Confirm from './Confirm'
import WidgetTitle from '../WidgetTitle'

const WidgetBody: FC<{ setFocusOnTitle: () => void }> = ({
  setFocusOnTitle,
}) => {
  const hostServices = useHostServices()
  const { hostData } = hostServices

  const renderTransfers = withTransferAccountsLoadState(getAllTransferAccounts)(
    hostData,
  )

  const [isRepeating, setIsRepeating] = useState(false)

  const onSave: OnSave<Transfer> = async (transfer, setItem) => {
    const updatedTransfer = await saveTransfer(transfer, hostServices)
    setItem(updatedTransfer)
  }

  const onRepeat: OnRepeat = () => {
    hostData.transferAccounts.update()
    setIsRepeating(true)
  }

  useEffect(() => {
    hostData.transferAccounts.update()
  }, [])

  const testId = 'transfer-spinner'

  return (
    <>
      {renderTransfers({
        unloaded: () => <LoadingSpinner testId={testId} />,
        loading: () => <LoadingSpinner testId={testId} />,
        loaded: accounts => (
          <CustomerLimitsProvider hostServices={hostServices}>
            <CreateFlow<Transfer>
              input={<Input accounts={accounts} />}
              review={<Review setFocusOnTitle={setFocusOnTitle} />}
              confirm={<Confirm />}
              onSave={onSave}
              onRepeat={onRepeat}
              /**
               * We only want to set focus if the user clicked the "Make another
               * transfer" button. Otherwise we will steal the focus when the
               * widget input form mounts for the first time
               */
              focusOnInit={isRepeating}
            />
          </CustomerLimitsProvider>
        ),
        failed: () => (
          <Alert
            disableContainer
            variant="error"
            heading="This information isn't available right now."
          />
        ),
      })}
    </>
  )
}

const TransfersWidget: FC = () => {
  const [titleRef, setFocusOnTitle] = useFocus()

  return (
    <Widget
      widgetTitle={<WidgetTitle title="Make a One-Time Transfer" />}
      dataTestId="transfer-widget"
      widgetBody={<WidgetBody setFocusOnTitle={setFocusOnTitle} />}
      titleRef={titleRef}
    />
  )
}

export default TransfersWidget
